// Counter Intelligence — setup-surface (Add Items onboarding page) helpers.
//
// The Add Items page (add-items.html) is the final step of first-run
// onboarding. A brand-new store's owner lands there after signup and STAYS
// there: until the store has real (non-sample) items — or the owner taps
// "Skip for now" — the onboarding gate on every other app page redirects
// straight back to Add Items (see public/js/auth-redirect.js). The drawer is
// mounted on that page too, so it needs to know when it is on this surface:
//   - link buttons that point at gated pages would just bounce the user back,
//   - the empty-state copy and starter prompts should be about adding items,
//   - support answers must teach the tools on THIS page, not "Inventory → Items".
//
// Pure (no React / no I/O) so it is unit-testable.

// True for the Add Items onboarding page, with or without query/hash. Accepts a
// pathname ("/add-items.html"), a bare surface string ("add-items.html"), or a
// same-site href ("/add-items.html#import").
export function isSetupSurfacePath(path) {
  const clean = String(path || '').split(/[?#]/)[0].trim();
  return /(^|\/)add-items(\.html)?$/i.test(clean);
}

// Should a chat message's link button be dropped? On the setup surface, links
// to other app pages are dead ends — the onboarding gate redirects them back
// here — so they are suppressed until this chat has actually created real
// items (`unlocked`: an in-chat import was saved or a single item was added).
// From that moment the store has non-sample items, the gate is open, and the
// links genuinely work. Links to the setup page itself and external links are
// always kept.
export function suppressChatLink({ onSetupSurface, unlocked, href } = {}) {
  if (!onSetupSurface || unlocked) return false;
  const h = String(href || '').trim();
  if (!h) return true; // no destination — nothing worth keeping
  if (/^https?:\/\//i.test(h)) return false; // external — the gate doesn't apply
  return !isSetupSurfacePath(h);
}

// Empty-state copy for the drawer on the setup surface: this user is brand new
// and mid-onboarding, so speak to the one job in front of them — getting items in.
export const SETUP_EMPTY_COPY = [
  'You’re on the Add Items step — I can help you get your items in, right here in this chat.',
  'Upload a product list, order guide, or invoice and I’ll import it; name a single item and I’ll add it; or ask how any option on this page works. Everything shows a preview before it saves.',
];

// Starter prompts behind an empty chat on the setup surface — one per job a
// brand-new user reaches for: understand the options, import a list, add one item.
export const SETUP_TRY_PROMPTS = [
  'How should I add my items?',
  'Import my order guide',
  'Add an item called Tito’s Vodka 1L, category Liquor, unit Bottle',
];
